import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getPrintOrderDetails, updateOrderStatus, updatePrintStatus } from '@app/services/order';
import { base64Decode } from '@app/helpers/base64url';
import { Button } from '@app/components';
import moment from 'moment';

const ORDER_STATUSES = [
    { label: 'Unpaid', value: 'unpaid' },
    { label: 'Sent to Print Partner', value: 'sent' },
    { label: 'In Production', value: 'acknowledged' },
    { label: 'Failed', value: 'failed' },
    { label: 'Dispatched', value: 'dispatched' },
    { label: 'Cancelled', value: 'cancelled' },
];

const ORDER_STATUS_BADGE: Record<string, string> = {
    sent: 'badge-warning',
    acknowledged: 'badge-info',
    dispatched: 'badge-success',
    unpaid: 'badge-secondary',
    failed: 'badge-danger',
    cancelled: 'badge-dark',
};

type PrintStatus = 'pending' | 'printed' | 'reprinting';

interface SetItem {
    id: number;
    name: string;
    qty: number;
    type: 'set' | 'handbook';
    print_status: PrintStatus;
    selected: boolean;
}

interface ModuleItem {
    name: string;
    sets: SetItem[];
    handbooks: SetItem[];
    expanded: boolean;
}

interface SubjectItem {
    name: string;
    modules: ModuleItem[];
    expanded: boolean;
}

const STATUS_BADGE: Record<PrintStatus, string> = {
    pending: 'badge-secondary',
    printed: 'badge-success',
    reprinting: 'badge-warning',
};

const PrintPanel = () => {
    const { encodedId } = useParams<{ encodedId: string }>();
    const orderId = encodedId ? base64Decode(encodedId) : undefined;
    const navigate = useNavigate();
    const canAccess = useSelector((state: any) => state.auth.permissions?.['print-operations']);

    const [isLoading, setLoading] = useState(false);
    const [order, setOrder] = useState<any>(null);
    const [subjects, setSubjects] = useState<SubjectItem[]>([]);
    const [isPrinting, setIsPrinting] = useState(false);

    useEffect(() => {
        if (canAccess?.['print-panel'] === false) {
            toast.error('You do not have permission to access this page');
            navigate('/');
            return;
        }
        if (orderId) fetchOrder();
    }, []);

    const fetchOrder = async () => {
        try {
            setLoading(true);
            const resp = await getPrintOrderDetails(orderId);
            if (resp?.success === true) {
                setOrder(resp.collections);
                buildHierarchy(resp.collections);
            } else {
                toast.error(resp?.message || 'Order not found');
                navigate('/print-operations');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal server error');
        } finally {
            setLoading(false);
        }
    };

    // Build subject → module → sets/handbooks hierarchy from API response
    const buildHierarchy = (orderData: any) => {
        const result: SubjectItem[] = [];
        const modules = orderData?.modules ?? {};

        Object.keys(modules).forEach((subjectKey) => {
            const moduleList = modules[subjectKey];
            if (!Array.isArray(moduleList) || moduleList.length === 0) return;

            const subjectItem: SubjectItem = {
                name: subjectKey.charAt(0).toUpperCase() + subjectKey.slice(1),
                expanded: true,
                modules: moduleList.map((mod: any) => {
                    // Map sets from API (only sets array)
                    const sets: SetItem[] = (mod.sets ?? [])
                        .filter((s: any) => s.id && s.qty > 0)
                        .map((s: any) => ({
                            id: s.id,
                            name: s.name || 'Unnamed Set',
                            qty: s.qty,
                            type: 'set' as const,
                            print_status: s.print_status || 'pending',
                            selected: false,
                        }));

                    // Map handbooks from API (only handbooks array)
                    const handbooks: SetItem[] = (mod.handbooks ?? [])
                        .filter((h: any) => h.id && h.qty > 0)
                        .map((h: any) => ({
                            id: h.id,
                            name: h.name || 'Unnamed Handbook',
                            qty: h.qty,
                            type: 'handbook' as const,
                            print_status: h.print_status || 'pending',
                            selected: false,
                        }));

                    return {
                        name: mod.name || 'Unnamed Module',
                        sets,
                        handbooks,
                        expanded: true,
                    };
                }),
            };

            // Only add subjects that have modules with items
            if (subjectItem.modules.some(m => m.sets.length > 0 || m.handbooks.length > 0)) {
                result.push(subjectItem);
            }
        });

        setSubjects(result);
    };

    // Selection helpers
    const toggleItem = (subIdx: number, modIdx: number, itemIdx: number, itemType: 'set' | 'handbook') => {
        setSubjects(prev => {
            const next = [...prev];
            const items = itemType === 'set' ? next[subIdx].modules[modIdx].sets : next[subIdx].modules[modIdx].handbooks;
            items[itemIdx].selected = !items[itemIdx].selected;
            return next;
        });
    };

    const toggleModule = (subIdx: number, modIdx: number, checked: boolean) => {
        setSubjects(prev => {
            const next = [...prev];
            next[subIdx].modules[modIdx].sets = next[subIdx].modules[modIdx].sets.map(s => ({ ...s, selected: checked }));
            next[subIdx].modules[modIdx].handbooks = next[subIdx].modules[modIdx].handbooks.map(h => ({ ...h, selected: checked }));
            return next;
        });
    };

    const toggleSubject = (subIdx: number, checked: boolean) => {
        setSubjects(prev => {
            const next = [...prev];
            next[subIdx].modules = next[subIdx].modules.map(m => ({
                ...m,
                sets: m.sets.map(s => ({ ...s, selected: checked })),
                handbooks: m.handbooks.map(h => ({ ...h, selected: checked })),
            }));
            return next;
        });
    };

    const toggleAll = (checked: boolean) => {
        setSubjects(prev =>
            prev.map(sub => ({
                ...sub,
                modules: sub.modules.map(m => ({
                    ...m,
                    sets: m.sets.map(s => ({ ...s, selected: checked })),
                    handbooks: m.handbooks.map(h => ({ ...h, selected: checked })),
                })),
            }))
        );
    };

    const toggleExpand = (subIdx: number, modIdx?: number) => {
        setSubjects(prev => {
            const next = [...prev];
            if (modIdx !== undefined) {
                next[subIdx].modules[modIdx].expanded = !next[subIdx].modules[modIdx].expanded;
            } else {
                next[subIdx].expanded = !next[subIdx].expanded;
            }
            return next;
        });
    };

    const getSelectedItems = () =>
        subjects.flatMap(s => s.modules.flatMap(m => [...m.sets.filter(set => set.selected), ...m.handbooks.filter(hb => hb.selected)]));

    const handlePrint = async (itemsToPrint: SetItem[]) => {
        if (itemsToPrint.length === 0) {
            toast.warning('Please select at least one item to print');
            return;
        }
        setIsPrinting(true);

        try {
            // Prepare payload for API
            const payload = itemsToPrint.map(item => ({
                id: item.id,
                type: item.type,
                print_status: item.print_status === 'printed' ? 'reprinting' : 'printed',
            }));

            // Update print status via API
            const resp = await updatePrintStatus(Number(orderId), payload);
            
            if (resp?.success) {
                // Generate print content
                const printContent = `
                    <html><head><title>Print Order #${order?.id}</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 20px; }
                        h2 { border-bottom: 2px solid #000; padding-bottom: 8px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 16px; }
                        th, td { border: 1px solid #ccc; padding: 8px 12px; text-align: left; }
                        th { background: #f5f5f5; font-weight: bold; }
                    </style></head>
                    <body>
                        <h2>Print Order #${order?.id} — ${order?.franchise_name}</h2>
                        <p>Printer Order No: ${order?.printer_order_no ?? '---'}</p>
                        <table>
                            <thead><tr><th>#</th><th>Item</th><th>Type</th><th>Qty</th></tr></thead>
                            <tbody>
                                ${itemsToPrint.map((item, i) => `
                                    <tr>
                                        <td>${i + 1}</td>
                                        <td>${item.name}</td>
                                        <td>${item.type.charAt(0).toUpperCase() + item.type.slice(1)}</td>
                                        <td>${item.qty}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                    </body></html>
                `;

                // Print using hidden iframe
                const iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                document.body.appendChild(iframe);
                const doc = iframe.contentDocument || iframe.contentWindow?.document;
                if (doc) {
                    doc.open();
                    doc.write(printContent);
                    doc.close();
                    iframe.contentWindow?.focus();
                    iframe.contentWindow?.print();
                }
                setTimeout(() => document.body.removeChild(iframe), 1000);

                // Update local state with new print status
                setSubjects(prev =>
                    prev.map(sub => ({
                        ...sub,
                        modules: sub.modules.map(m => ({
                            ...m,
                            sets: m.sets.map(s => {
                                const updated = payload.find(p => p.id === s.id && p.type === 'set');
                                return updated ? { ...s, print_status: updated.print_status as PrintStatus, selected: false } : s;
                            }),
                            handbooks: m.handbooks.map(h => {
                                const updated = payload.find(p => p.id === h.id && p.type === 'handbook');
                                return updated ? { ...h, print_status: updated.print_status as PrintStatus, selected: false } : h;
                            }),
                        })),
                    }))
                );

                toast.success(`${itemsToPrint.length} item(s) sent to print`);
            } else {
                toast.error(resp?.message || 'Failed to update print status');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to print items');
        } finally {
            setIsPrinting(false);
        }
    };

    const selectedItems = getSelectedItems();
    const allSelected = subjects.length > 0 && subjects.every(s => s.modules.every(m => 
        (m.sets.length === 0 || m.sets.every(s => s.selected)) && 
        (m.handbooks.length === 0 || m.handbooks.every(h => h.selected))
    ));
    const someSelected = selectedItems.length > 0;

    // Check if all items are printed
    const allItemsPrinted = subjects.every(s => s.modules.every(m => 
        m.sets.every(s => s.print_status === 'printed') && 
        m.handbooks.every(h => h.print_status === 'printed')
    ));

    const isDispatched = order?.order_status === 'dispatched';

    const handleMarkAsDispatched = async () => {
        if (!allItemsPrinted) {
            toast.warning('All items must be printed before marking as dispatched');
            return;
        }

        try {
            setLoading(true);
            const resp = await updateOrderStatus({
                order_id: Number(orderId),
                order_status: 'dispatched'
            });

            if (resp?.success) {
                toast.success('Order marked as dispatched successfully');
                setOrder({ ...order, order_status: 'dispatched' });
            } else {
                toast.error(resp?.message || 'Failed to update order status');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to update order status');
        } finally {
            setLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"><span className="sr-only">Loading...</span></div>
            </div>
        );
    }

    return (
        <div>
            <section className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1>Print Panel — Order #{order?.id}</h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="/">Home</a></li>
                                <li className="breadcrumb-item"><a href="/print-operations">Print Operations</a></li>
                                <li className="breadcrumb-item active">Print Panel</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </section>

            <section className="content">
                <div className="container-fluid">
                    {/* Order summary bar */}
                    {order && (
                        <div className="card mb-3">
                            <div className="card-body py-3">
                                <div className="row align-items-center">
                                    <div className="col-sm-3">
                                        <dt className="text-muted small mb-1">Franchise</dt>
                                        <dd className="mb-0 font-weight-bold">{order.franchise_name}</dd>
                                    </div>
                                    <div className="col-sm-2">
                                        <dt className="text-muted small mb-1">Printer No</dt>
                                        <dd className="mb-0">{order.printer_order_no ?? '---'}</dd>
                                    </div>
                                    <div className="col-sm-1">
                                        <dt className="text-muted small mb-1">Sets</dt>
                                        <dd className="mb-0">{order.qty_sets ?? 0}</dd>
                                    </div>
                                    <div className="col-sm-1">
                                        <dt className="text-muted small mb-1">Handbooks</dt>
                                        <dd className="mb-0">{order.qty_handbooks ?? 0}</dd>
                                    </div>
                                    <div className="col-sm-2">
                                        <dt className="text-muted small mb-1">Status</dt>
                                        <dd className="mb-0">
                                            <span className={`badge ${ORDER_STATUS_BADGE[order.order_status] ?? 'badge-secondary'}`}>
                                                {ORDER_STATUSES.find(s => s.value === order.order_status)?.label ?? order.order_status}
                                            </span>
                                        </dd>
                                    </div>
                                    <div className="col-sm-2">
                                        <dt className="text-muted small mb-1">Order Date</dt>
                                        <dd className="mb-0">{order.created_at ? moment(order.created_at).format('DD/MM/YYYY HH:mm') : '---'}</dd>
                                    </div>
                                    <div className="col-sm-1 text-right">
                                        <button className="btn btn-default btn-sm" onClick={() => navigate('/print-operations')}>
                                            <i className="fas fa-arrow-left mr-1" /> Back
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Action bar — hidden for dispatched orders */}
                    {!isDispatched && (
                    <div className="card card-body mb-3 d-flex flex-row align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            <div className="icheck-primary mr-3">
                                <input
                                    type="checkbox"
                                    id="select-all"
                                    checked={allSelected && subjects.length > 0}
                                    onChange={(e) => toggleAll(e.target.checked)}
                                />
                                <label htmlFor="select-all">Select All</label>
                            </div>
                            <span className="text-muted small">{selectedItems.length} item(s) selected</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <Button
                                isLoading={isPrinting}
                                disabled={!someSelected}
                                onClick={() => handlePrint(selectedItems)}
                                className="btn btn-primary mr-2"
                            >
                                <i className="fas fa-print mr-1" /> Print Selected ({selectedItems.length})
                            </Button>
                            {allItemsPrinted && (
                                <Button
                                    isLoading={isLoading}
                                    onClick={handleMarkAsDispatched}
                                    className="btn btn-success"
                                    title="Mark order as dispatched"
                                >
                                    <i className="fas fa-check-circle mr-1" /> Mark as Dispatched
                                </Button>
                            )}
                        </div>
                    </div>
                    )}

                    {isDispatched && (
                        <div className="alert alert-info mb-3">
                            <i className="fas fa-info-circle mr-2" />
                            This order has been dispatched. Printing is no longer available — view only.
                        </div>
                    )}

                    {/* Hierarchy */}
                    {subjects.length === 0 && !isLoading && (
                        <div className="alert alert-info">No printable items found in this order.</div>
                    )}

                    {subjects.map((subject, subIdx) => (
                        <div key={subject.name} className="card mb-3">
                            {/* Subject header */}
                            <div
                                className="card-header d-flex align-items-center"
                                style={{ cursor: 'pointer', backgroundColor: '#f0f4ff' }}
                            >
                                {!isDispatched && (
                                <div className="icheck-primary mr-2" onClick={e => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        id={`sub-${subIdx}`}
                                        checked={subject.modules.length > 0 && subject.modules.every(m => 
                                            (m.sets.length === 0 || m.sets.every(s => s.selected)) && 
                                            (m.handbooks.length === 0 || m.handbooks.every(h => h.selected))
                                        )}
                                        onChange={(e) => toggleSubject(subIdx, e.target.checked)}
                                    />
                                    <label htmlFor={`sub-${subIdx}`} />
                                </div>
                                )}
                                <h5 className="mb-0 flex-grow-1" onClick={() => toggleExpand(subIdx)}>
                                    <i className={`fas fa-chevron-${subject.expanded ? 'down' : 'right'} mr-2`} />
                                    <i className="fas fa-book-open mr-2 text-primary" />
                                    {subject.name}
                                </h5>
                            </div>

                            {subject.expanded && (
                                <div className="card-body p-0">
                                    {subject.modules.map((mod, modIdx) => (
                                        <div key={`${subject.name}-${modIdx}`} className="border-bottom">
                                            {/* Module header */}
                                            <div
                                                className="d-flex align-items-center px-4 py-2"
                                                style={{ backgroundColor: '#f8f9fa', cursor: 'pointer' }}
                                            >
                                                {!isDispatched && (
                                                <div className="icheck-primary mr-2" onClick={e => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        id={`mod-${subIdx}-${modIdx}`}
                                                        checked={(mod.sets.length === 0 || mod.sets.every(s => s.selected)) && 
                                                                 (mod.handbooks.length === 0 || mod.handbooks.every(h => h.selected))}
                                                        onChange={(e) => toggleModule(subIdx, modIdx, e.target.checked)}
                                                    />
                                                    <label htmlFor={`mod-${subIdx}-${modIdx}`} />
                                                </div>
                                                )}
                                                <span className="font-weight-bold flex-grow-1" onClick={() => toggleExpand(subIdx, modIdx)}>
                                                    <i className={`fas fa-chevron-${mod.expanded ? 'down' : 'right'} mr-2 text-muted`} />
                                                    <i className="fas fa-layer-group mr-2 text-secondary" />
                                                    {mod.name}
                                                </span>
                                                <span className="badge badge-light">{mod.sets.length + mod.handbooks.length} item(s)</span>
                                            </div>

                                            {/* Sets and Handbooks */}
                                            {mod.expanded && (
                                                <table className="table table-sm mb-0" style={{ width: '100%' }}>
                                                    <thead>
                                                        <tr style={{ backgroundColor: '#fff' }}>
                                                            {!isDispatched && <th style={{ width: 40 }}></th>}
                                                            <th style={{ width: '55%' }}>Item</th>
                                                            <th style={{ width: 70 }}>Type</th>
                                                            <th style={{ width: 50 }}>Qty</th>
                                                            <th style={{ width: 110 }}>Print Status</th>
                                                            {!isDispatched && <th style={{ width: 100 }}>Action</th>}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {mod.sets.map((set, setIdx) => (
                                                            <tr key={`set-${set.id}`} style={set.selected ? { borderLeft: '3px solid #007bff', backgroundColor: '#e8f0fe' } : {}}>
                                                                {!isDispatched && (
                                                                <td>
                                                                    <div className="icheck-primary">
                                                                        <input
                                                                            type="checkbox"
                                                                            id={`set-${set.id}`}
                                                                            checked={set.selected}
                                                                            onChange={() => toggleItem(subIdx, modIdx, setIdx, 'set')}
                                                                        />
                                                                        <label htmlFor={`set-${set.id}`} />
                                                                    </div>
                                                                </td>
                                                                )}
                                                                <td>{set.name}</td>
                                                                <td>
                                                                    <span className="badge badge-primary">Set</span>
                                                                </td>
                                                                <td>{set.qty}</td>
                                                                <td>
                                                                    <span className={`badge ${set.print_status ? STATUS_BADGE[set.print_status] : 'badge-secondary'}`}>
                                                                        {set.print_status ? set.print_status.charAt(0).toUpperCase() + set.print_status.slice(1) : 'Pending'}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {!isDispatched && (
                                                                        set.print_status === 'printed' ? (
                                                                            <button className="btn btn-xs btn-warning font-weight-bold" style={{ whiteSpace: 'nowrap' }} onClick={() => handlePrint([set])}>
                                                                                <i className="fas fa-redo mr-1" /> Reprint
                                                                            </button>
                                                                        ) : (
                                                                            <button className="btn btn-xs btn-primary font-weight-bold" style={{ whiteSpace: 'nowrap' }} onClick={() => handlePrint([set])}>
                                                                                <i className="fas fa-print mr-1" /> Print
                                                                            </button>
                                                                        )
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        {mod.handbooks.map((handbook, hbIdx) => (
                                                            <tr key={`hb-${handbook.id}`} style={handbook.selected ? { borderLeft: '3px solid #007bff', backgroundColor: '#e8f0fe' } : {}}>
                                                                {!isDispatched && (
                                                                <td>
                                                                    <div className="icheck-primary">
                                                                        <input
                                                                            type="checkbox"
                                                                            id={`hb-${handbook.id}`}
                                                                            checked={handbook.selected}
                                                                            onChange={() => toggleItem(subIdx, modIdx, hbIdx, 'handbook')}
                                                                        />
                                                                        <label htmlFor={`hb-${handbook.id}`} />
                                                                    </div>
                                                                </td>
                                                                )}
                                                                <td>{handbook.name}</td>
                                                                <td>
                                                                    <span className="badge badge-info">Handbook</span>
                                                                </td>
                                                                <td>{handbook.qty}</td>
                                                                <td>
                                                                    <span className={`badge ${handbook.print_status ? STATUS_BADGE[handbook.print_status] : 'badge-secondary'}`}>
                                                                        {handbook.print_status ? handbook.print_status.charAt(0).toUpperCase() + handbook.print_status.slice(1) : 'Pending'}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {!isDispatched && (
                                                                        handbook.print_status === 'printed' ? (
                                                                            <button className="btn btn-xs btn-warning font-weight-bold" style={{ whiteSpace: 'nowrap' }} onClick={() => handlePrint([handbook])}>
                                                                                <i className="fas fa-redo mr-1" /> Reprint
                                                                            </button>
                                                                        ) : (
                                                                            <button className="btn btn-xs btn-primary font-weight-bold" style={{ whiteSpace: 'nowrap' }} onClick={() => handlePrint([handbook])}>
                                                                                <i className="fas fa-print mr-1" /> Print
                                                                            </button>
                                                                        )
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default PrintPanel;
